import {
	DATE_FORMAT,
	DATE_FORMAT_EN,
	DATE_FORMAT_EN_SHORT,
	DATE_FORMAT_LOCAL,
	DATE_FORMAT_SERVER,
	DATE_FORMAT_SHORT,
	DATE_TIME_FORMAT,
	ISO_FORMAT,
	TIME_FORMAT,
	TIME_FORMAT_FULL,
	TIME_FORMAT_LOCAL,
	UNIX_FORMAT,
	UNIX_FORMAT_MS,
} from '@/constants'
import { dayJs as dayjs } from '@/plugins/Dayjs'
import { type Dayjs } from 'dayjs'
import { watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'

export function getValidDateObject(value: any) {
	let dateObj
	if (typeof value === 'number') {
		dateObj =
			value > 1e12 ? dayjs(value, UNIX_FORMAT_MS) : dayjs(value, UNIX_FORMAT)
	} else {
		dateObj = dayjs(
			value,
			[
				ISO_FORMAT,
				UNIX_FORMAT,
				DATE_FORMAT,
				DATE_FORMAT_SHORT,
				DATE_FORMAT_EN,
				DATE_FORMAT_EN_SHORT,
				DATE_FORMAT_SERVER,
				TIME_FORMAT,
				TIME_FORMAT_FULL,
			],
			true
		)
	}
	return dateObj.isValid() ? dateObj : undefined
}

export function normalizeDate(value: Dayjs) {
	return value.minute(0).second(0).millisecond(0)
}
export function dateGreaterThan(date: Dayjs, minDate: Dayjs) {
	return date.isAfter(minDate)
}
export function dateGreaterThanOrEqual(date: Dayjs, minDate: Dayjs) {
	return date.isSameOrAfter(minDate)
}
export function dateSmallerThanOrEqual(date: Dayjs, maxDate: Dayjs) {
	return date.isSameOrBefore(maxDate)
}
export function dateBetween(date: Dayjs, minDate: Dayjs, maxDate: Dayjs) {
	return date.isBetween(minDate, maxDate, 'date', '[]')
}

function combineDateWithTime(
	dateIsoString: string | Date,
	time: string | undefined = undefined
) {
	const dateObj = dayjs(dateIsoString)
	const timeObj = time ? dayjs(time, TIME_FORMAT) : dayjs().minute(0).second(0)

	if (!dateObj.isValid() || !timeObj.isValid()) {
		return undefined
	}

	return dateObj
		.hour(timeObj.hour())
		.minute(timeObj.minute())
		.second(timeObj.second())
		.millisecond(timeObj.millisecond())
}
function splitDateFromTime(dateIsoString: Date | string | null | undefined) {
	const dateObj = dayjs(dateIsoString)
	if (!dateObj.isValid()) return undefined
	return { date: dateObj.toDate(), time: dateObj.format(TIME_FORMAT) }
}

function getDateTime(value: any) {
	const date = getValidDateObject(value)
	if (!date) return date
	return {
		dateLocal: date.format(DATE_FORMAT_LOCAL),
		dateServer: date.format(DATE_FORMAT_SERVER),
		dateTimeLocal: date.format(DATE_TIME_FORMAT),
		timeLocal: date.format(TIME_FORMAT_LOCAL),
	}
}
function getLocalDate(value: any) {
	return getValidDateObject(value)?.format(DATE_FORMAT_LOCAL)
}
function getLocalTime(value: any) {
	return getValidDateObject(value)?.format(TIME_FORMAT_LOCAL)
}

function getLocalDateTime(value: any) {
	return getValidDateObject(value)?.format('DD.MM.YYYY - HH:mm:ss')
}

export const useDayJs = () => {
	const { locale } = useI18n()

	watchEffect(() => {
		dayjs.locale(locale.value)
	})

	function timeAgo(value: any, withoutSuffix: boolean = false) {
		void locale.value // ← register dependency on locale
		return getValidDateObject(value)?.fromNow(withoutSuffix)
	}

	return {
		dayjs,
		normalizeDate,
		dateGreaterThan,
		dateGreaterThanOrEqual,
		dateSmallerThanOrEqual,
		splitDateFromTime,
		combineDateWithTime,
		getValidDateObject,
		getDateTime,
		getLocalDate,
		getLocalTime,
		getLocalDateTime,
		timeAgo,
	}
}
